import { useNavigate } from '@tanstack/react-router';
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api/client';
import {
  useApproveArticle,
  useArchiveArticle,
  useDeleteArticle,
  useKnowledgeArticle,
  useKnowledgeTree,
  useUnarchiveArticle,
  useUpdateArticle,
} from '@/lib/api/knowledge-base';

import { ArticleForm, type ArticleFormValues, toArticlePayload } from './article-form';
import { StatusBadge, VisibilityBadge } from './kb-badges';
import { PrePublishChecklist } from './pre-publish-checklist';

export function ArticleEditPage({ articleId }: { articleId: string }) {
  const articleQuery = useKnowledgeArticle(articleId);
  const treeQuery = useKnowledgeTree();
  const updateArticle = useUpdateArticle(articleId);
  const approve = useApproveArticle();
  const archive = useArchiveArticle();
  const unarchive = useUnarchiveArticle();
  const deleteArticle = useDeleteArticle();
  const navigate = useNavigate();

  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);

  const article = articleQuery.data;
  const folders = treeQuery.data?.folders ?? [];

  if (articleQuery.isPending) {
    return <p className="text-sm">Loading…</p>;
  }
  if (articleQuery.isError || !article) {
    return (
      <p className="text-sm text-[var(--color-destructive)]">
        Failed to load the article: {articleQuery.error?.message ?? 'not found'}
      </p>
    );
  }

  const initial: ArticleFormValues = {
    title: article.title,
    slug: article.slug,
    folderId: article.folderId,
    visibility: article.visibility,
    ownerId: article.ownerId,
    questions: article.questions,
    tags: article.tags,
    body: article.body,
  };

  const actionBusy =
    approve.isPending || archive.isPending || unarchive.isPending || deleteArticle.isPending;

  const runAction = (
    mutate: (id: string, opts: { onError: (e: unknown) => void }) => void,
    fallback: string,
  ) => {
    setActionError(null);
    mutate(articleId, {
      onError: (caught) => setActionError(caught instanceof ApiError ? caught.message : fallback),
    });
  };

  return (
    <div className="space-y-6">
      <div className="sticky top-14 z-10 -mx-4 border-b bg-[var(--color-background)] px-4 py-3 md:-mx-6 md:px-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{article.title}</h1>
            <div className="mt-1 flex items-center gap-2">
              <StatusBadge status={article.status} />
              <VisibilityBadge visibility={article.visibility} />
              <span className="text-sm text-[var(--color-muted-foreground)]">
                Updated {new Date(article.updatedAt).toLocaleString()}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {article.status === 'draft' && (
              <div className="flex items-center">
                <Button
                  type="button"
                  className="rounded-r-none"
                  disabled={actionBusy}
                  onClick={() => runAction(approve.mutate, 'Could not approve the article.')}
                >
                  Approve
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-9 rounded-l-none border-l-0"
                  aria-label="Pre-publish checklist"
                  aria-expanded={showChecklist}
                  onClick={() => setShowChecklist((open) => !open)}
                >
                  {showChecklist ? (
                    <ChevronUp className="size-4" />
                  ) : (
                    <ChevronDown className="size-4" />
                  )}
                </Button>
              </div>
            )}
            {article.status !== 'archived' && (
              <Button
                type="button"
                variant="outline"
                disabled={actionBusy}
                onClick={() => runAction(archive.mutate, 'Could not archive the article.')}
              >
                Archive
              </Button>
            )}
            {article.status === 'archived' && (
              <Button
                type="button"
                variant="outline"
                disabled={actionBusy}
                onClick={() => runAction(unarchive.mutate, 'Could not unarchive the article.')}
              >
                Unarchive
              </Button>
            )}

            {confirmingDelete ? (
              <span className="flex items-center gap-2 text-sm">
                Delete permanently?
                <Button
                  type="button"
                  variant="outline"
                  className="text-[var(--color-destructive)]"
                  disabled={actionBusy}
                  onClick={() =>
                    deleteArticle.mutate(articleId, {
                      onSuccess: () => void navigate({ to: '/knowledge-base' }),
                      onError: (caught) =>
                        setActionError(
                          caught instanceof ApiError
                            ? caught.message
                            : 'Could not delete the article.',
                        ),
                    })
                  }
                >
                  Confirm delete
                </Button>
                <Button type="button" variant="outline" onClick={() => setConfirmingDelete(false)}>
                  Cancel
                </Button>
              </span>
            ) : (
              <Button
                type="button"
                variant="ghost"
                className="text-[var(--color-destructive)]"
                onClick={() => setConfirmingDelete(true)}
              >
                Delete
              </Button>
            )}
          </div>
        </div>

        <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
          Lifecycle actions apply to the saved article — save your edits first.
        </p>
        {actionError && (
          <p className="mt-1 text-sm text-[var(--color-destructive)]">{actionError}</p>
        )}
        {article.status === 'draft' && showChecklist && (
          <div className="mt-3 max-w-xl">
            <PrePublishChecklist />
          </div>
        )}
      </div>

      <ArticleForm
        initial={initial}
        folders={folders}
        submitting={updateArticle.isPending}
        submitLabel="Save changes"
        error={error}
        header={
          article.status === 'approved' ? (
            <div className="flex items-start gap-2 rounded-md border border-[var(--color-destructive)] p-3 text-sm">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-[var(--color-destructive)]" />
              <span>
                This article is <strong>approved</strong>. Saving an edit will unpublish it — it
                returns to <strong>draft</strong> and disappears from the agents until re-approved.
              </span>
            </div>
          ) : undefined
        }
        onCancel={() => void navigate({ to: '/knowledge-base' })}
        onSubmit={(values) => {
          setError(null);
          updateArticle.mutate(toArticlePayload(values), {
            onError: (caught) =>
              setError(caught instanceof ApiError ? caught.message : 'Could not save the article.'),
          });
        }}
      />
    </div>
  );
}
